import {
  type InteractiveAuthResponse,
  InteractiveAuthResponseEnum,
} from "@b2bportal/card-api";
import {
  FulfillResponseEnum,
  type CipherText,
  type FulfillResponse,
} from "@b2bportal/purchase-api";
import { CallState } from "@hopper-b2b/types";
import { assign, type DoneInvokeEvent } from "xstate";
import {
  buildDuplicateBookingSignature,
  type DuplicateBookingSignatureInput,
} from "../../../duplicateBooking/signature";
import {
  recordCompletedBooking,
  recordPendingBooking,
} from "../../../duplicateBooking/store";
import { getObjectKeysAsObject, setContextWithKey } from "../../../helpers";
import type { CartFulfillError } from "../../../types";
import { ParentState } from "../../../types";
import type { CartContext } from "../context";
import { CartState } from "../types";
import type { CartFulfillContext } from "./context";
import type { CompleteInteractiveSuccess } from "./events";

// --- Duplicate-booking lifecycle helpers (shared, flag-gated) ---
// The stay signature and the enable flag live on the parent checkout context,
// not on CartContext, so read them structurally.
type DuplicateBookingReadableContext = {
  enableHotelDuplicateBookingWarning?: boolean;
  [key: string]: unknown;
};

const asDuplicateBookingContext = (
  ctx: CartContext
): DuplicateBookingReadableContext =>
  ctx as unknown as DuplicateBookingReadableContext;

const duplicateBookingSignatureFor = (ctx: CartContext): string | null =>
  buildDuplicateBookingSignature(
    asDuplicateBookingContext(ctx)[
      ParentState.lodgingShop
    ] as DuplicateBookingSignatureInput
  );

const isDuplicateBookingWarningEnabled = (ctx: CartContext): boolean =>
  Boolean(asDuplicateBookingContext(ctx).enableHotelDuplicateBookingWarning);

export const actions = {
  setCartFulfillCipherText: assign(
    (ctx: CartContext, event: DoneInvokeEvent<CipherText>) =>
      setContextWithKey(ctx, CartState.cartFulfill, {
        ...ctx[CartState.cartFulfill],
        fulfillCipherText: event.data,
      } as CartFulfillContext)
  ),
  setCartFulfillError: assign(
    (ctx: CartContext, event: DoneInvokeEvent<CartFulfillError>) =>
      setContextWithKey(ctx, CartState.cartFulfill, {
        ...ctx[CartState.cartFulfill],
        error: event.data,
      } as CartFulfillContext)
  ),
  setCartFulfillResult: assign(
    (ctx: CartContext, event: DoneInvokeEvent<FulfillResponse>) => {
      switch (event.data.FulfillResponse) {
        case FulfillResponseEnum.Success: {
          return setContextWithKey(ctx, CartState.cartFulfill, {
            ...ctx[CartState.cartFulfill],
            fulfilledPayments: event.data.payments,
            fulfilledProducts: event.data.products,
            notPurchasedProducts: event.data.notPurchasedProducts,
            fulfillRequestPayments: {},
            trackingProperties: event.data.trackingProperties,
          } as CartFulfillContext);
        }
        default:
          return ctx;
      }
    }
  ),
  clearCartFulfillError: assign((ctx: CartContext) =>
    setContextWithKey(ctx, CartState.cartFulfill, {
      ...ctx[CartState.cartFulfill],
      error: undefined,
    } as CartFulfillContext)
  ),
  setCartFulfillCallStateInProcess: assign((ctx: CartContext) =>
    setContextWithKey(ctx, CartState.cartFulfill, {
      ...ctx[CartState.cartFulfill],
      callState: CallState.InProcess,
    } as CartFulfillContext)
  ),
  setCartFulfillCallStateSuccess: assign((ctx: CartContext) =>
    setContextWithKey(ctx, CartState.cartFulfill, {
      ...ctx[CartState.cartFulfill],
      callState: CallState.Success,
    } as CartFulfillContext)
  ),
  setCartFulfillCallStateFailed: assign((ctx: CartContext) =>
    setContextWithKey(ctx, CartState.cartFulfill, {
      ...ctx[CartState.cartFulfill],
      callState: CallState.Failed,
    } as CartFulfillContext)
  ),
  setCartFulfillPendingInteractive: assign(
    (ctx: CartContext, event: DoneInvokeEvent<InteractiveAuthResponse>) => {
      switch (event.data.InteractiveAuthResponse) {
        case InteractiveAuthResponseEnum.PendingInteractivePaymentAuth: {
          return setContextWithKey(ctx, CartState.cartFulfill, {
            ...ctx[CartState.cartFulfill],
            pendingInteractive: event.data,
          } as CartFulfillContext);
        }
        default:
          return setContextWithKey(ctx, CartState.cartFulfill, {
            ...ctx[CartState.cartFulfill],
            pendingInteractive: undefined,
          } as CartFulfillContext);
      }
    }
  ),
  clearCartFulfillPendingInteractive: assign((ctx: CartContext) =>
    setContextWithKey(ctx, CartState.cartFulfill, {
      ...ctx[CartState.cartFulfill],
      pendingInteractive: undefined,
    } as CartFulfillContext)
  ),
  clearCartFulfillUserActions: assign((ctx: CartContext) =>
    setContextWithKey(ctx, CartState.cartFulfill, {
      ...ctx[CartState.cartFulfill],
      userAction: undefined,
    } as CartFulfillContext)
  ),
  setCartFulfillCompleteInteractive: assign(
    (ctx: CartContext, event: CompleteInteractiveSuccess) =>
      setContextWithKey(ctx, CartState.cartFulfill, {
        ...ctx[CartState.cartFulfill],
        completeInteractive: { results: [event.data] },
      } as CartFulfillContext)
  ),

  // Duplicate-booking lifecycle (shared, flag-gated). Recording the
  // stay signature lives here — in the shared fulfillment machine — so any
  // tenant that sets `enableHotelDuplicateBookingWarning` gets consistent
  // behavior and cannot mis-wire the pending/completed lifecycle. All are
  // no-ops when the flag is off or a signature cannot be built (fail open).

  // Record the stay as pending once fulfillment is scheduled (committed
  // server-side): if the tab reloads before confirmation, a re-book is caught.
  recordPendingDuplicateBooking: (ctx: CartContext) => {
    if (isDuplicateBookingWarningEnabled(ctx)) {
      recordPendingBooking(duplicateBookingSignatureFor(ctx));
    }
  },
  // On definitive fulfillment success, replace pending with completed so a
  // rapid re-book of the same stay is warned against.
  completeDuplicateBooking: (ctx: CartContext) => {
    if (isDuplicateBookingWarningEnabled(ctx)) {
      recordCompletedBooking(duplicateBookingSignatureFor(ctx));
    }
  },
};

export const ActionTypes = getObjectKeysAsObject(actions);
